import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, UsersIcon, CircleCheckIcon, RecycleIcon, HomeIcon, TruckIcon, Trash2Icon, ArrowUpRightIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_CONTAINER = 'https://cdn.magicpatterns.com/patterns/generated-images/42283f42-4328-471a-814a-38b21dee0cd6.jpg';
const PHOTO_RECYCLING = 'https://cdn.magicpatterns.com/patterns/generated-images/78feed8d-084e-4f94-958f-192b393364ec.jpg';

/**
 * Research source — edit here once a verified reference is confirmed.
 * While `url` is empty, the button is shown as non-interactive.
 */
const RESEARCH_SOURCE = {
  url: '',
  label: {
    fr: 'Source : études municipales et publications académiques sur les applications de recyclage résidentiel.',
    en: 'Source: municipal studies and academic publications on residential recycling applications.'
  }
};
const reveal = (delay = 0) => ({
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.45,
    delay,
    ease: EASE
  }
});
function IconCircle({
  icon: I
}: {
  icon: typeof MapPinIcon;
}) {
  return <span className="w-14 h-14 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-2030" data-mp="2hr2ku6g25x4j">
      <I className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-2031" data-mp="2dqipn8f1p3l1" />
    </span>;
}
function StatusLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-block rounded-md bg-[#F1F0FD] px-2.5 py-1.5 font-sans text-[10.5px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] leading-snug" data-id="element-2032" data-mp="1l2ei0hcwygfp">
      {children}
    </span>;
}
const card = 'rounded-3xl border border-[#E7E8F0] bg-white shadow-[0_2px_12px_rgba(18,18,18,0.03)] overflow-hidden';
export function CollectProof() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const t = (fr: string, en: string) => isFR ? fr : en;
  const collections = [{
    color: 'bg-[#3A3A3A]',
    label: t('Ordures', 'Garbage'),
    date: t('Jeudi 14 mai', 'Thursday, May 14')
  }, {
    color: 'bg-[#4F46E5]',
    label: t('Recyclage', 'Recycling'),
    date: t('Jeudi 21 mai', 'Thursday, May 21')
  }, {
    color: 'bg-[#8A5A3B]',
    label: t('Matières organiques', 'Organics'),
    date: t('Jeudi 28 mai', 'Thursday, May 28')
  }];
  const proofPoints = [t('Calendriers personnalisés', 'Personalized collection calendars'), t('Information sur les matières et services de collecte', 'Material and collection service information'), t('Intelligence agentique intégrée', 'Integrated agentic intelligence')];
  const canadaPins = [{
    x: 30,
    y: 62
  }, {
    x: 42,
    y: 66
  }, {
    x: 64,
    y: 74
  }, {
    x: 196,
    y: 58
  }, {
    x: 204,
    y: 90
  }, {
    x: 180,
    y: 104
  }, {
    x: 192,
    y: 112
  }, {
    x: 205,
    y: 110
  }, {
    x: 232,
    y: 88
  }];
  return <div className="max-w-[1440px] mx-auto" data-id="element-2033" data-mp="cn9rgugtx6xz">
      {/* Header */}
      <motion.div {...reveal()} className="text-center max-w-3xl mx-auto" data-id="element-2034" data-mp="cpm7h231178e">
        <p className="font-sans text-xs font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-2035" data-mp="1x5al10l6yrui">
          {t('Éprouvé sur le terrain', 'Proven in the field')}
        </p>
        <h2 className="font-serif text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] text-[#121212] leading-[1.08] tracking-tight mt-5" data-id="element-2036" data-mp="1nez3gb8l4k55">
          {t('Déployé. Utilisé. En expansion.', 'Deployed. Used. Expanding.')}
        </h2>
        <p className="font-sans text-[17px] text-[#5B6478] leading-relaxed mt-5" data-id="element-2037" data-mp="1dje64qego81q">
          {t('Des communautés utilisent déjà le module Collectes ATNA pour mieux informer leurs citoyens et simplifier la gestion des matières résiduelles.', 'Communities already use ATNA Waste Collection to better inform residents and simplify waste management.')}
        </p>
      </motion.div>

      {/* Proof cards */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)_minmax(0,0.95fr)] gap-5" data-id="element-2038" data-mp="2twcg6qrmt6n7">
        {/* CARD 1 — Clarence-Rockland */}
        <motion.article {...reveal(0.04)} className={`${card} md:col-span-2 xl:col-span-1`} data-id="element-2039" data-mp="1rgk83tl39m2o">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] h-full" data-id="element-2040" data-mp="1vi5uq6nfdeq2">
            <div className="p-7 lg:pr-3" data-id="element-2041" data-mp="1hmlbtb7p4win">
              <div className="flex items-center gap-4" data-id="element-2042" data-mp="1mj29aw0scn8e">
                <IconCircle icon={MapPinIcon} data-id="element-2043" data-mp="1mloif03brasq" />
                <div className="min-w-0" data-id="element-2044" data-mp="17ufk82lcwh3z">
                  <h3 className="font-serif text-[2rem] text-[#121212] leading-none tracking-tight" data-id="element-2045" data-mp="17te9y0c5zynb">Clarence-Rockland</h3>
                  <p className="font-sans text-[15px] text-[#8A90A0] mt-1.5" data-id="element-2046" data-mp="1cn8n0kx9lfdf">Ontario</p>
                </div>
              </div>
              <div className="mt-6" data-id="element-2047" data-mp="13ski4vvlpzp5">
                <StatusLabel data-id="element-2048" data-mp="13tlud3ocss8g">{t('Collectes ATNA en production', 'ATNA Waste Collection in production')}</StatusLabel>
              </div>
              <ul className="mt-6 space-y-4" data-id="element-2049" data-mp="tuwo685tkw81">
                {proofPoints.map(p => <li key={p} className="flex items-start gap-3" data-id="element-2050" data-mp="twqyytndn7ye">
                    <CircleCheckIcon className="w-5 h-5 shrink-0 text-[#4F46E5] mt-0.5" strokeWidth={1.7} aria-hidden="true" data-id="element-2051" data-mp="a9mp894543e9" />
                    <span className="font-sans text-[15px] text-[#121212] leading-snug" data-id="element-2052" data-mp="69vrtmk3yp0r">{p}</span>
                  </li>)}
              </ul>
            </div>

            {/* Product visualization */}
            <div className="relative flex items-end justify-center gap-3 px-5 pt-6 lg:pt-10 min-h-[300px]" data-id="element-2053" data-mp="t7yxlsn8998s">
              {/* Phone */}
              <div className="relative w-[148px] shrink-0 rounded-t-[26px] border-[5px] border-b-0 border-[#121212] bg-white px-3 pt-6 pb-4 shadow-[0_8px_24px_rgba(18,18,18,0.12)]" data-id="element-2054" data-mp="p5c7bw67fwdp">
                <span className="absolute left-1/2 top-1.5 -translate-x-1/2 w-12 h-3 rounded-full bg-[#121212]" aria-hidden="true" data-id="element-2055" data-mp="xyfdrkifn8yu" />
                <p className="font-serif text-[14px] text-[#121212] text-center" data-id="element-2056" data-mp="1cwhhpvtdlwgm">{t('Où ça va ?', 'Where does it go?')}</p>
                <img src={PHOTO_CONTAINER} alt={t('Un contenant de plastique.', 'A plastic container.')} loading="lazy" className="w-full h-[70px] rounded-lg object-cover mt-2.5" data-id="element-2057" data-mp="17us9sp1g9ywr" />
                <p className="font-sans text-[10px] text-[#121212] text-center leading-snug mt-3" data-id="element-2058" data-mp="1oxh9tnl1rou0">
                  {t('Ce contenant va dans le bac de recyclage.', 'This container goes in the recycling bin.')}
                </p>
                <span className="mx-auto mt-3 w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-2059" data-mp="1yqetvy5otluq">
                  <RecycleIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-2060" data-mp="27oqqqhx5dfvm" />
                </span>
                <span className="block mt-3 rounded-md bg-[#4F46E5] py-1.5 font-sans text-[9px] text-white text-center" data-id="element-2061" data-mp="2co3gbafel11w">
                  {t('Voir les consignes', 'View guidelines')}
                </span>
              </div>

              {/* Upcoming collections */}
              <div className="self-start w-[170px] shrink-0 rounded-2xl border border-[#EDEEF3] bg-white p-3.5 shadow-[0_6px_20px_rgba(18,18,18,0.06)]" data-id="element-2062" data-mp="aqb39bamcst8">
                <p className="font-sans text-[13px] font-medium text-[#121212]" data-id="element-2063" data-mp="5y044anpdb1q">
                  {t('Prochaines collectes', 'Upcoming collections')}
                </p>
                <ul className="mt-2 divide-y divide-[#EDEEF3]" data-id="element-2064" data-mp="3s7kkf65yqs1b">
                  {collections.map(c => <li key={c.label} className="flex items-center gap-2.5 py-2.5" data-id="element-2065" data-mp="to47cgwzb8s7">
                      <span className={`w-3.5 h-5 shrink-0 rounded-[3px] ${c.color}`} aria-hidden="true" data-id="element-2066" data-mp="opsvy1r1cq7v" />
                      <span className="min-w-0" data-id="element-2067" data-mp="jpy5u0f344nv">
                        <span className="block font-sans text-[12px] text-[#121212] leading-tight" data-id="element-2068" data-mp="jssqnt20frht">{c.label}</span>
                        <span className="block font-sans text-[11px] text-[#8A90A0] leading-tight mt-0.5" data-id="element-2069" data-mp="34h70y41g21ic">{c.date}</span>
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </motion.article>

        {/* CARD 2 — Bonnyville */}
        <motion.article {...reveal(0.08)} className={`${card} flex flex-col p-7`} data-id="element-2070" data-mp="3u5m78rcgbt2v">
          <div className="flex items-center gap-4" data-id="element-2071" data-mp="3g5upcfitdsjl">
            <IconCircle icon={MapPinIcon} data-id="element-2072" data-mp="3g7e421otnvj9" />
            <div className="min-w-0" data-id="element-2073" data-mp="3k6wluolcjqhv">
              <h3 className="font-serif text-[2rem] text-[#121212] leading-none tracking-tight" data-id="element-2074" data-mp="3k7y0t45hgx4v">Bonnyville</h3>
              <p className="font-sans text-[15px] text-[#8A90A0] mt-1.5" data-id="element-2075" data-mp="brwetihczimn">Alberta</p>
            </div>
          </div>
          <div className="mt-5 sm:pl-[72px]" data-id="element-2076" data-mp="gsk49zpxvrer">
            <StatusLabel data-id="element-2077" data-mp="2zui9vaeumhn">{t('Suivi des collectes en temps réel', 'Real-time collection tracking')}</StatusLabel>
            <p className="font-sans text-[14.5px] text-[#5B6478] leading-relaxed mt-4" data-id="element-2078" data-mp="6zl7hukaguk2">
              {t('Visualisation du parcours des collectes en temps réel sur une carte.', 'Real-time visualization of collection routes on a map.')}
            </p>
          </div>

          <div className="relative mt-6 flex-1 min-h-[200px] rounded-2xl overflow-hidden border border-[#EDEEF3] bg-[#F4F5F3]" data-id="element-2079" data-mp="2k5rvjf5ibitm">
            <svg viewBox="0 0 320 200" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-2080" data-mp="2f6f6qov2s1oe">
              <path d="M180 -10 C 170 60, 230 110, 210 210" stroke="#D4E3EE" strokeWidth="16" fill="none" data-id="element-2081" data-mp="2oxjd506oqe7m" />
              <path d="M0 60 L 320 40 M 0 150 L 320 170 M 70 0 L 110 200 M 260 0 L 240 200" stroke="#FFFFFF" strokeWidth="4" fill="none" data-id="element-2082" data-mp="217p12ku6wtof" />
              <path d="M48 150 C 80 140, 100 90, 130 78 M 130 78 C 160 110, 190 130, 214 140 C 236 150, 250 160, 272 158" stroke="#4F46E5" strokeWidth="2.5" fill="none" strokeDasharray="0" data-id="element-2083" data-mp="1wb89thrp1z74" />
              {[{
              x: 214,
              y: 140
            }, {
              x: 240,
              y: 152
            }].map(p => <circle key={p.x} cx={p.x} cy={p.y} r="3.5" fill="#FFFFFF" stroke="#4F46E5" strokeWidth="1.8" data-id="element-2084" data-mp="3hnjjl3gu3zcl" />)}
            </svg>
            <span className="absolute left-[15%] top-[75%] -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-[0_2px_8px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-2085" data-mp="110bodk4uwvka">
              <HomeIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-2086" data-mp="r9gvhwx3uxpv" />
            </span>
            <span className="absolute left-[40.5%] top-[39%] -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#4F46E5] border-2 border-white shadow-[0_2px_8px_rgba(79,70,229,0.3)] flex items-center justify-center" data-id="element-2087" data-mp="mae2jaueghy9">
              <TruckIcon className="w-4 h-4 text-white" strokeWidth={2} aria-hidden="true" data-id="element-2088" data-mp="8bxlor2tpb2e" />
            </span>
            <span className="absolute left-[86%] top-[79%] -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-[0_2px_8px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-2089" data-mp="3e5vq2lqnz0u">
              <Trash2Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-2090" data-mp="1xr44uivpcpml" />
            </span>
            <div className="absolute right-3 top-3 rounded-xl bg-white px-3.5 py-2.5 shadow-[0_4px_14px_rgba(18,18,18,0.08)]" data-id="element-2091" data-mp="25a4z0dkm56xa">
              <p className="flex items-center gap-2 font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-2092" data-mp="2a7wq55dfop0f">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-2093" data-mp="2f2st30k7kwsb" />
                {t('Camion de recyclage', 'Recycling truck')}
              </p>
              <p className="font-sans text-[11.5px] text-[#8A90A0] mt-1 pl-3.5" data-id="element-2094" data-mp="1mdll7ayv6uue">{t('En cours de collecte', 'Collection in progress')}</p>
              <p className="font-sans text-[11.5px] text-[#8A90A0] pl-3.5" data-id="element-2095" data-mp="1rcyg8izw3882">{t('Secteur Ouest', 'West sector')}</p>
            </div>
          </div>
        </motion.article>

        {/* CARD 3 — Network */}
        <motion.article {...reveal(0.12)} className={`${card} flex flex-col p-7`} data-id="element-2096" data-mp="37pdms7o5pkz1">
          <div className="flex items-start gap-4" data-id="element-2097" data-mp="37rhqaoi58hys">
            <IconCircle icon={UsersIcon} data-id="element-2098" data-mp="3cnf3ily4187c" />
            <p className="font-serif text-[#121212] leading-[1.02] tracking-tight" data-id="element-2099" data-mp="3cqkrtyyx0620">
              <span className="block text-[2.6rem]" data-id="element-2100" data-mp="1ckdwrk5ud1wj">{t('Plus de 20', '20+')}</span>
              <span className="block text-[2rem]" data-id="element-2101" data-mp="17mcf93o2dan6">{t('communautés', 'communities')}</span>
            </p>
          </div>
          <div className="mt-5" data-id="element-2102" data-mp="1lm3x5fhpbb6g">
            <StatusLabel data-id="element-2103" data-mp="1hll45i42nz2o">{t('En cours de déploiement', 'Deployment in progress')}</StatusLabel>
          </div>
          <p className="font-sans text-[14.5px] text-[#5B6478] leading-relaxed mt-4" data-id="element-2104" data-mp="tv7lgi7dnckr">
            {t('Le module Collectes ATNA est déployé progressivement dans l’ensemble de notre réseau de communautés au Québec et au Canada.', 'ATNA Waste Collection, progressively deployed across our network of communities in Quebec and Canada.')}
          </p>
          <div className="mt-auto pt-6" data-id="element-2105" data-mp="a7i3f2p8sqoq">
            <svg viewBox="0 0 280 150" className="w-full h-auto" aria-hidden="true" data-id="element-2106" data-mp="a6fa6o4g6c5n">
              <path d="M14 58 L 30 40 L 52 44 L 70 22 L 96 30 L 110 14 L 138 24 L 150 10 L 172 20 L 184 8 L 200 26 L 214 22 L 224 42 L 242 50 L 256 70 L 270 82 L 262 98 L 240 100 L 226 116 L 212 118 L 206 132 L 190 126 L 176 114 L 150 110 L 126 104 L 96 102 L 70 100 L 48 98 L 30 94 L 22 80 Z" fill="#EEEFF3" stroke="#E2E4EA" strokeWidth="1" data-id="element-2107" data-mp="653kkilkpqxg" />
              <path d="M176 60 C 186 74, 196 76, 204 70" stroke="#FFFFFF" strokeWidth="6" fill="none" data-id="element-2108" data-mp="3n90w66i655q8" />
              {canadaPins.map((p, i) => <g key={i} transform={`translate(${p.x} ${p.y})`} data-id="element-2109" data-mp="jrir26ts6cti" data-mpi={i}>
                  <path d="M0 0 C -5 -6, -5 -12, 0 -14 C 5 -12, 5 -6, 0 0 Z" fill="#4F46E5" data-id="element-2110" data-mp="jpptri5254y6" />
                  <circle cx="0" cy="-9.5" r="1.8" fill="#FFFFFF" data-id="element-2111" data-mp="4wmc7argur7g" />
                </g>)}
            </svg>
          </div>
        </motion.article>
      </div>

      {/* Research band */}
      <motion.div {...reveal(0.16)} className="mt-5 rounded-3xl border border-[#E7E8F0] bg-[#FAFAFE] p-4 lg:p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,2fr)_minmax(0,0.9fr)] gap-6 lg:gap-8 items-center" data-id="element-2112" data-mp="2pmv8z4htl1e6">
        <img src={PHOTO_RECYCLING} alt={t('Un bac de recyclage résidentiel rempli de bouteilles.', 'A residential recycling bin filled with bottles.')} loading="lazy" className="w-full h-[150px] rounded-2xl object-cover" data-id="element-2113" data-mp="35zq8rm7qml6b" />
        <div className="px-2 lg:px-0" data-id="element-2114" data-mp="2w0967dns8nek">
          <p className="font-sans text-[11px] font-semibold tracking-[0.22em] uppercase text-[#4F46E5]" data-id="element-2115" data-mp="3tq41ley5co1y">
            {t('Pourquoi l’information fait la différence', 'Why better information matters')}
          </p>
          <p className="font-serif text-[1.7rem] lg:text-[2rem] text-[#121212] leading-[1.15] mt-3" data-id="element-2116" data-mp="3jqd8nhp8ih3z">
            {t('Le bon geste commence par la bonne information.', 'The right action starts with the right information.')}
          </p>
          <p className="font-sans text-[15.5px] text-[#5B6478] leading-relaxed mt-3" data-id="element-2117" data-mp="28a52z7eiapo3">
            {t('La recherche montre que des interventions numériques d’éducation au tri peuvent contribuer à réduire les erreurs de tri et la contamination.', 'Research shows that digital sorting education interventions can help reduce sorting errors and recycling contamination.')}
          </p>
        </div>
        <div className="px-2 lg:px-0 lg:pl-8 lg:border-l lg:border-[#E7E8F0] lg:py-3" data-id="element-2118" data-mp="xkju46cts7g5">
          {RESEARCH_SOURCE.url ? <a href={RESEARCH_SOURCE.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-xl border border-[#4F46E5] px-5 py-3.5 font-sans text-[15px] text-[#4F46E5] transition-colors hover:bg-[#F1F0FD]" data-id="element-2119" data-mp="12ilcep2fg2qk">
              {t('Voir la recherche', 'View the research')}
              <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-2120" data-mp="3dcg1ln0hot6z" />
            </a> : <span aria-disabled="true" className="flex items-center justify-center gap-2 rounded-xl border border-[#4F46E5] px-5 py-3.5 font-sans text-[15px] text-[#4F46E5]" data-id="element-2121" data-mp="3ib11tmfqrcvf">
              {t('Voir la recherche', 'View the research')}
              <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-2122" data-mp="3k90rcid60z4p" />
            </span>}
          <p className="font-sans text-[11.5px] text-[#8A90A0] leading-relaxed mt-3" data-id="element-2123" data-mp="3f7bgp3tuunh5">
            {isFR ? RESEARCH_SOURCE.label.fr : RESEARCH_SOURCE.label.en}
          </p>
        </div>
      </motion.div>

      {/* Trust line */}
      <div className="mt-10 flex items-center gap-6" data-id="element-2124" data-mp="3p59difps5sld">
        <span className="flex-1 h-px bg-[#E4E6EC]" aria-hidden="true" data-id="element-2125" data-mp="6rrq6ckkjj5s" />
        <p className="font-sans text-[14px] text-[#5B6478] text-center" data-id="element-2126" data-mp="1thve32rakkp">
          {t('Une infrastructure BCITI | ATNA éprouvée depuis 2017.', 'BCITI | ATNA infrastructure, proven since 2017.')}
        </p>
        <span className="flex-1 h-px bg-[#E4E6EC]" aria-hidden="true" data-id="element-2127" data-mp="bsyxybmpoicg" />
      </div>
    </div>;
}